import React from 'react';
import { Crown, Gem, Sparkles } from 'lucide-react';

export interface VipBadgeProps {
  vipTier?: number | string | null;
  vipExpireAt?: string | Date | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showExpireTooltip?: boolean;
  className?: string;
}

/**
 * Format ngày hết hạn VIP theo chuẩn Ngày/Tháng/Năm (DD/MM/YYYY)
 */
export const formatVipExpireDate = (dateVal?: string | Date | null): string => {
  if (!dateVal) return 'Vĩnh viễn';
  try {
    const d = typeof dateVal === 'string' ? new Date(dateVal) : dateVal;
    if (!d || isNaN(d.getTime())) return 'Vĩnh viễn';
    return d.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return 'Vĩnh viễn';
  }
};

export const VipBadge: React.FC<VipBadgeProps> = ({
  vipTier,
  vipExpireAt,
  size = 'sm',
  showExpireTooltip = true,
  className = ''
}) => {
  const tierNum = typeof vipTier === 'number' ? vipTier : (vipTier ? Number(vipTier) : 0);

  // Không hiển thị nếu vipTier === 0 hoặc không hợp lệ
  if (tierNum !== 1 && tierNum !== 2) {
    return null;
  }

  const expireDateText = formatVipExpireDate(vipExpireAt);
  const tooltipText = `Hạn VIP: ${expireDateText}`;

  // Kích thước các phần tử
  const sizeStyles = {
    xs: {
      container: 'px-1.5 py-0.5 text-[9px] gap-0.5 rounded-md font-black tracking-wider',
      icon: 'w-2.5 h-2.5'
    },
    sm: {
      container: 'px-2 py-0.5 text-[10px] sm:text-[11px] gap-1 rounded-md font-black tracking-wider shadow-2xs',
      icon: 'w-3 h-3'
    },
    md: {
      container: 'px-2.5 py-1 text-xs gap-1.5 rounded-lg font-black tracking-wider shadow-xs',
      icon: 'w-3.5 h-3.5'
    },
    lg: {
      container: 'px-3.5 py-1.5 text-sm gap-1.5 rounded-xl font-black tracking-wider shadow-sm',
      icon: 'w-4 h-4'
    }
  }[size];

  // Tier 1: VIP (Gold / Amber Luxury)
  if (tierNum === 1) {
    return (
      <span
        title={showExpireTooltip ? tooltipText : undefined}
        className={`inline-flex items-center select-none bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-amber-950 border border-amber-300/80 shadow-amber-500/20 font-black uppercase transition-transform hover:scale-105 ${sizeStyles.container} ${className}`}
      >
        <Crown className={`${sizeStyles.icon} text-amber-950 fill-amber-950/20 shrink-0`} />
        <span>VIP</span>
      </span>
    );
  }

  // Tier 2: SVIP (Red Ruby Gradient / Diamond Luxury)
  return (
    <span
      title={showExpireTooltip ? tooltipText : undefined}
      className={`inline-flex items-center select-none bg-gradient-to-r from-rose-600 via-red-500 to-amber-500 text-white border border-rose-300/90 shadow-red-500/30 font-black uppercase transition-transform hover:scale-105 ${sizeStyles.container} ${className}`}
    >
      <Gem className={`${sizeStyles.icon} text-white fill-white/20 shrink-0 animate-pulse`} />
      <span>SVIP</span>
    </span>
  );
};

export default VipBadge;
